// redux/ugc/testimoniesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AxiosError } from "axios";
import { testimoniesApi } from "../../services/ugc/testimoniesApi";
import type {
  TestimoniesSummary,
  TestimonyDTO,
  TestimonyType,
} from "../../types/ugc/testimonyTypes";

type Status =
  | "idle"
  | "loading"
  | "succeeded"
  | "failed"
  | "converting";



export type RejectedPayload = {
  status?: number;
  data?: any;
  code?: "not_ready";
  reason?: "media_converting";
};

// Optional progress/abort hooks for uploads
type ProgressArg = {
  onUploadProgress?: (pct: number) => void;
  signal?: AbortSignal;
};

interface TestimoniesState {
  summary: TestimoniesSummary | null;
  summaryStatus: Status;
  detail: Record<string, TestimonyDTO | undefined>;     // key = slug
  detailStatus: Record<string, Status>;                  // key = slug
  opStatus: Record<string, Status>;                      // keys like create:audio, update:video:<slug>, delete:audio:<slug>
  error?: string | null;
}

const initialState: TestimoniesState = {
  summary: null,
  summaryStatus: "idle",
  detail: {},
  detailStatus: {},
  opStatus: {},
  error: null,
};

// ---------- helpers ----------
const extractApiError = (action: any): string => {
  const p = action.payload as RejectedPayload | undefined;
  if (p?.data) {
    if (typeof p.data === "string") return p.data;
    if (typeof p.data.detail === "string") return p.data.detail;
    try {
      return JSON.stringify(p.data);
    } catch {
      /* noop */
    }
  }
  return action.error?.message ?? "Request failed";
};

// ---------- Thunks with proper rejectValue ----------
export const fetchSummary = createAsyncThunk<
  TestimoniesSummary,
  void,
  { rejectValue: RejectedPayload }
>("testimonies/fetchSummary", async (_, { rejectWithValue }) => {
  try {
    return await testimoniesApi.getSummary();
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});


export const fetchDetailBySlug = createAsyncThunk<
  TestimonyDTO,
  string,
  { rejectValue: RejectedPayload }
>("testimonies/fetchDetailBySlug", async (slug, { rejectWithValue }) => {
  try {
    return await testimoniesApi.getDetailBySlug(slug);
  } catch (err) {
    const e = err as AxiosError<any>;
    const status = e.response?.status;

    // if media is converting
    if (status === 404) {
      return rejectWithValue({
        status,
        code: "not_ready",
        reason: "media_converting",
        data: e.response?.data,
      });
    }

    return rejectWithValue({ status, data: e.response?.data });
  }
});


export const createTestimony = createAsyncThunk<
  { id: number; slug: string; type: TestimonyType },
  { type: TestimonyType; payload: FormData | Record<string, any> } & ProgressArg,
  { rejectValue: RejectedPayload }
>("testimonies/create", async ({ type, payload, onUploadProgress, signal }, { rejectWithValue }) => {
  try {
    const res = await testimoniesApi.create(type, payload, { onUploadProgress, signal });
    return { ...res, type };
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});

export const updateTestimonyBySlug = createAsyncThunk<
  TestimonyDTO,
  { type: TestimonyType; slug: string; payload: FormData | Record<string, any> } & ProgressArg,
  { rejectValue: RejectedPayload }
>("testimonies/updateBySlug", async ({ type, slug, payload, onUploadProgress, signal }, { rejectWithValue }) => {
  try {
    return await testimoniesApi.updateBySlug(type, slug, payload, { onUploadProgress, signal });
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});

export const deleteTestimonyBySlug = createAsyncThunk<
  { type: TestimonyType; slug: string; ok: boolean },
  { type: TestimonyType; slug: string },
  { rejectValue: RejectedPayload }
>("testimonies/deleteBySlug", async ({ type, slug }, { rejectWithValue }) => {
  try {
    const ok = await testimoniesApi.removeBySlug(slug);
    return { type, slug, ok };
  } catch (err) {
    const e = err as AxiosError;
    return rejectWithValue({ status: e.response?.status, data: e.response?.data });
  }
});


// ---------- Slice ----------
const testimoniesSlice = createSlice({
  name: "testimonies",
  initialState,
  reducers: {
    resetOpStatus(state, action: PayloadAction<string | undefined>) {
      if (action.payload) {
        delete state.opStatus[action.payload];
      } else {
        state.opStatus = {};
      }
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // summary
    builder
      .addCase(fetchSummary.pending, (state) => {
        state.summaryStatus = "loading";
      })
      .addCase(fetchSummary.fulfilled, (state, action: PayloadAction<TestimoniesSummary>) => {
        state.summaryStatus = "succeeded";
        state.summary = action.payload;
      })
      .addCase(fetchSummary.rejected, (state, action) => {
        state.summaryStatus = "failed";
        state.error = extractApiError(action);
      });

    // detail (slug)
    builder
      .addCase(fetchDetailBySlug.pending, (state, action) => {
        const slug = action.meta.arg as string;
        state.detailStatus[slug] = "loading";
      })
      .addCase(fetchDetailBySlug.fulfilled, (state, action: PayloadAction<TestimonyDTO>) => {
        const dto = action.payload;
        state.detail[dto.slug] = dto;
        state.detailStatus[dto.slug] = "succeeded";

        // keep summary fresh for this type if already exists
        if (!state.summary) return;
        const bucket = (state.summary as any)[dto.type];
        if (bucket) {
          (state.summary as any)[dto.type] = {
            exists: true,
            id: dto.id,
            slug: dto.slug,
            title: dto.title,
            published_at: dto.published_at,
            is_converted: dto.is_converted,
            excerpt: bucket.excerpt,
          };
        }
      })
      .addCase(fetchDetailBySlug.rejected, (state, action) => {
        const slug = action.meta.arg as string;
        const payload = action.payload as RejectedPayload | undefined;

        // ✅ 404/not_ready => converting
        if (payload?.code === "not_ready") {
          state.detailStatus[slug] = "converting";
          return;
        }

        state.detailStatus[slug] = "failed";
        state.error = extractApiError(action);
      });


    // create
    builder
      .addCase(createTestimony.pending, (state, action) => {
        const k = `create:${(action.meta.arg as any).type}`;
        state.opStatus[k] = "loading";
      })
      .addCase(
        createTestimony.fulfilled,
        (state, action: PayloadAction<{ id: number; slug: string; type: TestimonyType }>) => {
          const { id, slug, type } = action.payload;
          const k = `create:${type}`;
          state.opStatus[k] = "succeeded";

          // optimistic summary update — show presence immediately
          if (!state.summary) {
            state.summary = {
              audio: { exists: false },
              video: { exists: false },
              written: { exists: false },
            } as unknown as TestimoniesSummary;
          }
          (state.summary as any)[type] = {
            exists: true,
            id,
            slug,
            // For better UX, default convert state: written=true, media=false until detail lands
            is_converted: type === "written" ? true : false,
          };
        }
      )
      .addCase(createTestimony.rejected, (state, action) => {
        const k = `create:${(action.meta.arg as any).type}`;
        state.opStatus[k] = "failed";
        state.error = extractApiError(action);
      });

    // update (slug)
    builder
      .addCase(updateTestimonyBySlug.pending, (state, action) => {
        const { type, slug } = action.meta.arg as any;
        state.opStatus[`update:${type}:${slug}`] = "loading";
      })
      .addCase(updateTestimonyBySlug.fulfilled, (state, action) => {
        const dto = action.payload as TestimonyDTO;
        const { type } = dto;
        const oldSlug = action.meta.arg.slug as string;

        // clean old keys if slug changed
        if (oldSlug && oldSlug !== dto.slug) {
          delete state.detail[oldSlug];
          delete state.detailStatus[oldSlug];
          delete state.opStatus[`update:${type}:${oldSlug}`];
        }

        state.detail[dto.slug] = dto;
        state.detailStatus[dto.slug] = "succeeded";
        state.opStatus[`update:${type}:${dto.slug}`] = "succeeded";

        if (state.summary) {
          (state.summary as any)[type] = {
            exists: true,
            id: dto.id,
            slug: dto.slug,
            title: dto.title,
            published_at: dto.published_at,
            is_converted: dto.is_converted,
            excerpt: (state.summary as any)[type]?.excerpt,
          };
        }
      })
      .addCase(updateTestimonyBySlug.rejected, (state, action) => {
        const { type, slug } = action.meta.arg as any;
        state.opStatus[`update:${type}:${slug}`] = "failed";
        state.error = extractApiError(action);
      });

    // delete (slug)
    builder
      .addCase(deleteTestimonyBySlug.pending, (state, action) => {
        const { type, slug } = action.meta.arg as any;
        state.opStatus[`delete:${type}:${slug}`] = "loading";
      })
      .addCase(
        deleteTestimonyBySlug.fulfilled,
        (state, action: PayloadAction<{ type: TestimonyType; slug: string; ok: boolean }>) => {
          const { type, slug } = action.payload;
          state.opStatus[`delete:${type}:${slug}`] = "succeeded";
          delete state.detail[slug];
          delete state.detailStatus[slug];
          if (state.summary) {
            (state.summary as any)[type] = { exists: false };
          }
        }
      )
      .addCase(deleteTestimonyBySlug.rejected, (state, action) => {
        const { type, slug } = action.meta.arg as any;
        state.opStatus[`delete:${type}:${slug}`] = "failed";
        state.error = extractApiError(action);
      });
  },
});

export const { resetOpStatus, clearError } = testimoniesSlice.actions;
export default testimoniesSlice.reducer;
